/**
 * 禁府面板组件
 * 显示监狱管理和间谍系统
 */

import { useState } from 'react'
import { useSecretPoliceStore } from '../../stores/secretPoliceStore'
import './SecretPolicePanel.css'

interface SecretPolicePanelProps {
  factionId: string
}

export function SecretPolicePanel({ factionId }: SecretPolicePanelProps) {
  const [activeTab, setActiveTab] = useState<'prisons' | 'prisoners' | 'spies' | 'intelligence' | 'missions' | 'events'>('prisons')
  const [selectedPrisoner, setSelectedPrisoner] = useState<string | null>(null)
  const [selectedSpy, setSelectedSpy] = useState<string | null>(null)
  
  const {
    prisoners,
    spies,
    intelligence,
    prisons,
    spyMissions,
    events,
    capturePrisoner,
    interrogatePrisoner,
    executePrisoner,
    releasePrisoner,
    deploySpy,
    recallSpy,
    analyzeIntelligence,
    buildPrison,
    upgradePrison,
    assignSpyMission,
    completeSpyMission
  } = useSecretPoliceStore()
  
  // 过滤当前势力的监狱
  const factionPrisons = Array.from(prisons.values()).filter(prison => prison.factionId === factionId)
  
  // 过滤当前势力的囚犯
  const factionPrisoners = Array.from(prisoners.values()).filter(prisoner => prisoner.capturedFactionId === factionId)
  
  // 过滤当前势力的间谍
  const factionSpies = Array.from(spies.values()).filter(spy => spy.factionId === factionId)
  
  // 过滤当前势力的情报
  const factionIntelligence = Array.from(intelligence.values()).filter(intel => intel.sourceFactionId === factionId)
  
  // 过滤当前势力的间谍任务
  const factionSpyMissions = Array.from(spyMissions.values()).filter(mission => {
    const spy = spies.get(mission.spyId)
    return spy?.factionId === factionId
  })
  
  // 处理捕获囚犯
  const handleCapturePrisoner = () => {
    if (factionPrisons.length > 0) {
      capturePrisoner('char_enemy_1', '商纣王', 'faction_shang', factionId, factionPrisons[0].id)
    }
  }
  
  // 处理部署间谍
  const handleDeploySpy = () => {
    deploySpy('char_spy_1', '间谍001', factionId, 'faction_shang', {
      name: '商人',
      occupation: '商人',
      background: '来自西方的商人'
    })
  }
  
  // 处理分配间谍任务
  const handleAssignSpyMission = () => {
    if (factionSpies.length > 0) {
      assignSpyMission('刺探商军情报', 'gather_intelligence', factionSpies[0].id, 'faction_shang', {
        location: '朝歌'
      })
    }
  }
  
  // 处理建造监狱
  const handleBuildPrison = () => {
    buildPrison('洛邑监狱', 'city_luoyang', factionId)
  }
  
  // 处理审讯囚犯
  const handleInterrogate = (prisonerId: string) => {
    interrogatePrisoner(prisonerId)
  }
  
  // 处理处决囚犯
  const handleExecute = (prisonerId: string) => {
    executePrisoner(prisonerId)
  }
  
  // 处理释放囚犯
  const handleRelease = (prisonerId: string) => {
    releasePrisoner(prisonerId)
  }
  
  // 处理召回间谍
  const handleRecallSpy = (spyId: string) => {
    recallSpy(spyId)
  }
  
  // 处理完成间谍任务
  const handleCompleteMission = (missionId: string, success: boolean) => {
    completeSpyMission(missionId, success, success ? '任务成功' : '任务失败')
  }
  
  return (
    <div className="secret-police-panel">
      {/* 标签页 */}
      <div className="secret-police-tabs">
        <button 
          className={`secret-police-tab ${activeTab === 'prisons' ? 'active' : ''}`}
          onClick={() => setActiveTab('prisons')}
        >
          监狱
        </button>
        <button 
          className={`secret-police-tab ${activeTab === 'prisoners' ? 'active' : ''}`}
          onClick={() => setActiveTab('prisoners')}
        >
          囚犯
        </button>
        <button 
          className={`secret-police-tab ${activeTab === 'spies' ? 'active' : ''}`}
          onClick={() => setActiveTab('spies')}
        >
          间谍
        </button>
        <button 
          className={`secret-police-tab ${activeTab === 'intelligence' ? 'active' : ''}`}
          onClick={() => setActiveTab('intelligence')}
        >
          情报
        </button>
        <button 
          className={`secret-police-tab ${activeTab === 'missions' ? 'active' : ''}`}
          onClick={() => setActiveTab('missions')}
        >
          任务
        </button>
        <button 
          className={`secret-police-tab ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          事件
        </button>
      </div>
      
      {/* 内容区域 */}
      <div className="secret-police-content">
        {/* 监狱管理 */}
        {activeTab === 'prisons' && (
          <div className="prisons-section">
            <div className="section-header">
              <h3>监狱管理</h3>
              <button 
                className="build-prison-btn"
                onClick={handleBuildPrison}
              >
                建造监狱
              </button>
            </div>
            
            <div className="prisons-list">
              {factionPrisons.map(prison => (
                <div key={prison.id} className="prison-card">
                  <div className="prison-header">
                    <h4>{prison.name}</h4>
                    <span className="prison-status">
                      {prison.status === 'operating' ? '运行中' : prison.status === 'under_construction' ? '建设中' : '损坏'}
                    </span>
                  </div>
                  <div className="prison-details">
                    <span>城市: {prison.cityId}</span>
                    <span>安全等级: {prison.securityLevel}/10</span>
                    <span>囚犯: {prison.prisoners.length}</span>
                    <span>守卫: {prison.guards.length}</span>
                  </div>
                  <div className="prison-facilities">
                    <h5>设施:</h5>
                    <div className="facilities-list">
                      {prison.facilities.map((facility, index) => (
                        <div key={index} className="facility-item">
                          <span>{getFacilityTypeText(facility.type)}</span>
                          <span>容量: {facility.capacity}</span>
                          <span>Lv.{facility.level}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="prison-actions">
                    <button 
                      className="upgrade-btn"
                      onClick={() => upgradePrison(prison.id)}
                    >
                      升级
                    </button>
                    <button 
                      className="manage-btn"
                      onClick={handleCapturePrisoner}
                    >
                      关押囚犯
                    </button>
                  </div>
                </div>
              ))}
              
              {factionPrisons.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">🏛️</span>
                  <p>暂无监狱</p>
                  <button 
                    className="build-prison-btn small"
                    onClick={handleBuildPrison}
                  >
                    建造第一个监狱
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 囚犯管理 */}
        {activeTab === 'prisoners' && (
          <div className="prisoners-section">
            <div className="section-header">
              <h3>囚犯管理</h3>
              <button 
                className="capture-prisoner-btn"
                onClick={handleCapturePrisoner}
              >
                捕获囚犯
              </button>
            </div>
            
            <div className="prisoners-list">
              {factionPrisoners.map(prisoner => (
                <div 
                  key={prisoner.id}
                  className={`prisoner-card ${prisoner.status}`}
                  onClick={() => setSelectedPrisoner(prisoner.id)}
                >
                  <div className="prisoner-header">
                    <h4>{prisoner.name}</h4>
                    <span className={`prisoner-status ${prisoner.status}`}>
                      {getPrisonerStatusText(prisoner.status)}
                    </span>
                  </div>
                  <div className="prisoner-details">
                    <span>所属势力: {prisoner.factionId}</span>
                    <span>监狱: {prisoner.prisonId}</span>
                    <span>捕获时间: {formatDate(prisoner.capturedAt)}</span>
                  </div>
                  {prisoner.crimes.length > 0 && (
                    <div className="prisoner-crimes">
                      <h5>罪行:</h5>
                      {prisoner.crimes.map((crime, index) => (
                        <span key={index} className="crime-tag">{crime}</span>
                      ))}
                    </div>
                  )}
                  <div className="prisoner-interrogation">
                    <h5>审讯:</h5>
                    <div className="interrogation-info">
                      <span>抵抗程度: {prisoner.interrogation.resistance}%</span>
                      <span>是否突破: {prisoner.interrogation.broken ? '是' : '否'}</span>
                    </div>
                    {prisoner.interrogation.informationGathered.length > 0 && (
                      <div className="gathered-info">
                        <h6>获得情报:</h6>
                        {prisoner.interrogation.informationGathered.map((info, index) => (
                          <span key={index} className="info-tag">{info}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="prisoner-actions">
                    {prisoner.status === 'detained' && (
                      <button 
                        className="interrogate-btn"
                        onClick={() => handleInterrogate(prisoner.id)}
                      >
                        审讯
                      </button>
                    )}
                    {prisoner.status === 'trialed' && prisoner.sentence.type === 'execution' && (
                      <button 
                        className="execute-btn"
                        onClick={() => handleExecute(prisoner.id)}
                      >
                        处决
                      </button>
                    )}
                    <button 
                      className="release-btn"
                      onClick={() => handleRelease(prisoner.id)}
                    >
                      释放
                    </button>
                  </div>
                </div>
              ))}
              
              {factionPrisoners.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">🔒</span>
                  <p>暂无囚犯</p>
                  <button 
                    className="capture-prisoner-btn small"
                    onClick={handleCapturePrisoner}
                  >
                    捕获第一个囚犯
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 间谍管理 */}
        {activeTab === 'spies' && (
          <div className="spies-section">
            <div className="section-header">
              <h3>间谍管理</h3>
              <button 
                className="deploy-spy-btn"
                onClick={handleDeploySpy}
              >
                部署间谍
              </button>
            </div>
            
            <div className="spies-list">
              {factionSpies.map(spy => (
                <div 
                  key={spy.id}
                  className={`spy-card ${spy.status}`}
                  onClick={() => setSelectedSpy(spy.id)}
                >
                  <div className="spy-header">
                    <h4>{spy.name}</h4>
                    <span className={`spy-status ${spy.status}`}>
                      {getSpyStatusText(spy.status)}
                    </span>
                  </div>
                  <div className="spy-details">
                    <span>目标势力: {spy.targetFactionId}</span>
                    <span>忠诚度: {spy.loyalty}%</span>
                    <span>暴露风险: {spy.exposureRisk}%</span>
                  </div>
                  <div className="spy-cover">
                    <h5>伪装身份:</h5>
                    <div className="cover-info">
                      <span>姓名: {spy.coverIdentity.name}</span>
                      <span>职业: {spy.coverIdentity.occupation}</span>
                    </div>
                  </div>
                  <div className="spy-skills">
                    <h5>技能:</h5>
                    <div className="skills-grid">
                      <div className="skill-item">
                        <span>潜行: {spy.skills.stealth}</span>
                      </div>
                      <div className="skill-item">
                        <span>欺骗: {spy.skills.deception}</span>
                      </div>
                      <div className="skill-item">
                        <span>战斗: {spy.skills.combat}</span>
                      </div>
                      <div className="skill-item">
                        <span>情报: {spy.skills.intelligence}</span>
                      </div>
                      <div className="skill-item">
                        <span>破坏: {spy.skills.sabotage}</span>
                      </div>
                    </div>
                  </div>
                  {spy.currentMissionId && (
                    <div className="spy-mission">
                      <h5>当前任务:</h5>
                      <span>{spyMissions.get(spy.currentMissionId)?.name || '未知任务'}</span>
                    </div>
                  )}
                  <div className="spy-actions">
                    <button 
                      className="recall-btn"
                      onClick={() => handleRecallSpy(spy.id)}
                    >
                      召回
                    </button>
                    <button 
                      className="assign-mission-btn"
                      onClick={handleAssignSpyMission}
                    >
                      分配任务
                    </button>
                  </div>
                </div>
              ))}
              
              {factionSpies.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">🕵️</span>
                  <p>暂无间谍</p>
                  <button 
                    className="deploy-spy-btn small"
                    onClick={handleDeploySpy}
                  >
                    部署第一个间谍
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 情报管理 */}
        {activeTab === 'intelligence' && (
          <div className="intelligence-section">
            <div className="section-header">
              <h3>情报管理</h3>
            </div>
            
            <div className="intelligence-list">
              {factionIntelligence.map(intel => (
                <div key={intel.id} className={`intelligence-card ${intel.level}`}>
                  <div className="intelligence-header">
                    <h4>{intel.title}</h4>
                    <span className={`intelligence-level ${intel.level}`}>
                      {getIntelligenceLevelText(intel.level)}
                    </span>
                  </div>
                  <div className="intelligence-details">
                    <span>类型: {getIntelligenceTypeText(intel.type)}</span>
                    <span>来源: {intel.sourceFactionId}</span>
                    <span>目标: {intel.targetFactionId}</span>
                    <span>状态: {intel.status === 'raw' ? '原始' : intel.status === 'analyzed' ? '已分析' : '已过时'}</span>
                  </div>
                  <div className="intelligence-content">
                    <p>{intel.content}</p>
                  </div>
                  <div className="intelligence-value">
                    <span>价值: {intel.value}/100</span>
                  </div>
                  {intel.status === 'raw' && (
                    <div className="intelligence-actions">
                      <button 
                        className="analyze-btn"
                        onClick={() => analyzeIntelligence(intel.id)}
                      >
                        分析
                      </button>
                    </div>
                  )}
                </div>
              ))}
              
              {factionIntelligence.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">📋</span>
                  <p>暂无情报</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 间谍任务 */}
        {activeTab === 'missions' && (
          <div className="missions-section">
            <div className="section-header">
              <h3>间谍任务</h3>
              <button 
                className="assign-mission-btn"
                onClick={handleAssignSpyMission}
              >
                分配任务
              </button>
            </div>
            
            <div className="missions-list">
              {factionSpyMissions.map(mission => (
                <div key={mission.id} className={`mission-card ${mission.status}`}>
                  <div className="mission-header">
                    <h4>{mission.name}</h4>
                    <span className={`mission-status ${mission.status}`}>
                      {getMissionStatusText(mission.status)}
                    </span>
                  </div>
                  <div className="mission-details">
                    <span>类型: {getMissionTypeText(mission.type)}</span>
                    <span>目标势力: {mission.targetFactionId}</span>
                    <span>难度: {mission.difficulty}/10</span>
                  </div>
                  <div className="mission-risks">
                    <h5>风险:</h5>
                    <div className="risks-list">
                      <span>被捕获: {mission.risks.capture}%</span>
                      <span>死亡: {mission.risks.death}%</span>
                      <span>暴露: {mission.risks.exposure}%</span>
                    </div>
                  </div>
                  <div className="mission-rewards">
                    <h5>奖励:</h5>
                    <div className="rewards-list">
                      <span>金钱: {mission.rewards.money}</span>
                      <span>经验: {mission.rewards.experience}</span>
                    </div>
                  </div>
                  {mission.status === 'in_progress' && (
                    <div className="mission-actions">
                      <button 
                        className="complete-btn success"
                        onClick={() => handleCompleteMission(mission.id, true)}
                      >
                        任务成功
                      </button>
                      <button 
                        className="complete-btn failure"
                        onClick={() => handleCompleteMission(mission.id, false)}
                      >
                        任务失败
                      </button>
                    </div>
                  )}
                </div>
              ))}
              
              {factionSpyMissions.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">🎯</span>
                  <p>暂无间谍任务</p>
                  <button 
                    className="assign-mission-btn small"
                    onClick={handleAssignSpyMission}
                  >
                    分配第一个任务
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 事件管理 */}
        {activeTab === 'events' && (
          <div className="events-section">
            <div className="section-header">
              <h3>禁府事件</h3>
            </div>
            
            <div className="events-list">
              {Array.from(events.values()).map(event => (
                <div key={event.id} className="event-card">
                  <div className="event-header">
                    <span className="event-type">{getEventTypeText(event.type)}</span>
                    <span className="event-date">{formatDate(event.date)}</span>
                  </div>
                  <div className="event-content">
                    <p>{event.description}</p>
                  </div>
                </div>
              ))}
              
              {events.size === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">📅</span>
                  <p>暂无禁府事件</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* 选中囚犯详情 */}
      {selectedPrisoner && (
        <div className="prisoner-detail-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>囚犯详情</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedPrisoner(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* 详细信息 */}
            </div>
          </div>
        </div>
      )}
      
      {/* 选中间谍详情 */}
      {selectedSpy && (
        <div className="spy-detail-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>间谍详情</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedSpy(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* 详细信息 */}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// 辅助函数
function getFacilityTypeText(type: string): string {
  const typeTexts = {
    detention_cell: '拘留室',
    prison_cell: '监狱',
    interrogation_room: '审讯室',
    execution_ground: '刑场',
    security_room: '安全室'
  }
  return typeTexts[type as keyof typeof typeTexts] || '未知设施'
}

function getPrisonerStatusText(status: string): string {
  const statusTexts = {
    detained: '被拘留',
    trialed: '已审判',
    imprisoned: '已监禁',
    executed: '已处决',
    released: '已释放',
    escaped: '已逃脱'
  }
  return statusTexts[status as keyof typeof statusTexts] || '未知状态'
}

function getSpyStatusText(status: string): string {
  const statusTexts = {
    infiltrating: '渗透中',
    active: '活跃',
    compromised: '已暴露',
    extracted: '已撤回',
    dead: '已死亡'
  }
  return statusTexts[status as keyof typeof statusTexts] || '未知状态'
}

function getIntelligenceTypeText(type: string): string {
  const typeTexts = {
    military: '军事',
    economic: '经济',
    diplomatic: '外交',
    internal: '内政',
    personal: '个人'
  }
  return typeTexts[type as keyof typeof typeTexts] || '未知类型'
}

function getIntelligenceLevelText(level: string): string {
  const levelTexts = {
    low: '低',
    medium: '中',
    high: '高',
    critical: '关键'
  }
  return levelTexts[level as keyof typeof levelTexts] || '未知等级'
}

function getMissionTypeText(type: string): string {
  const typeTexts = {
    gather_intelligence: '收集情报',
    sabotage: '破坏',
    assassination: '暗杀',
    infiltrate: '渗透',
    counter_intelligence: '反情报'
  }
  return typeTexts[type as keyof typeof typeTexts] || '未知类型'
}

function getMissionStatusText(status: string): string {
  const statusTexts = {
    planned: '计划中',
    in_progress: '进行中',
    completed: '已完成',
    failed: '失败',
    cancelled: '已取消'
  }
  return statusTexts[status as keyof typeof statusTexts] || '未知状态'
}

function getEventTypeText(type: string): string {
  const typeTexts = {
    prisoner: '囚犯',
    spy: '间谍',
    intelligence: '情报',
    security: '安全'
  }
  return typeTexts[type as keyof typeof typeTexts] || '未知事件'
}

function formatDate(date: { year: number; month: number; day: number }): string {
  return `${date.year}年${date.month}月${date.day}日`
}
